import React, { HTMLAttributes, HTMLProps } from 'react';

import { Image, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import { Button } from '../../../components';
import useClasses from '../../../hooks/useClasses';
import type { MediaReferenceField } from '../../../types/uncinc_profile/helpers/media';
import type { ImmutableParagraphTextImageEntity } from '..';

import './style.scss';

interface Props extends HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableParagraphTextImageEntity;
  index?: number;
}

interface MediaProps {
  media: MediaReferenceField<'image' | 'illustration_svg_' | 'vector_image'>;
}

const Media: React.FC<MediaProps> = ({ media }) => {
  switch (media.bundle) {
    case 'image': {
      return (
        <Image
          src={media.styles.original}
          alt={media.entity.field_media_image[0].alt}
          lazyload={false}
        />
      );
    }

    case 'illustration_svg_':
    case 'vector_image': {
      return (
        <img
          src={media.entity.field_media_file[0].url}
          alt={media.entity.field_media_file[0].description}
          width="100%"
        />
      );
    }

    default: {
      return null;
    }
  }
};

const ParagraphTextImage: React.FC<Props> = ({
  entity,
  className,
  index,
}) => {
  const { buildClass, joinClasses } = useClasses('ParagraphTextImage');

  const paragraph = entity.toJS();
  const title = paragraph.field_title[0].value;
  const body = paragraph.field_text[0].processed;
  const link = paragraph.field_link?.[0];
  const media = paragraph.field_media_item[0];

  const orderClassesMedia = 'order-lg-2 offset-lg-1 order-md-2 order-sm-1 offset-md-0 media';
  const orderClassesContent = 'order-lg-1 order-md-1 order-sm-1 offset-md-0 content';

  const gridClassesMedia = `col-lg-5 col-md-6 col-sm-12 col-12 ${(index % 2 === 1) ? orderClassesMedia : orderClassesContent}`;
  const gridClassesContent = `col-lg-6 col-md-6 col-sm-12 col-12 ${(index % 2 === 1) ? orderClassesContent : orderClassesMedia}`;

  return (
    <article className={joinClasses(className, buildClass())}>
      <div className="row">
        <div className={joinClasses(buildClass('content-container'), gridClassesContent)}>
          <div className={buildClass('title')}>
            <h2>{title}</h2>
          </div>

          <div className={buildClass('body')}>
            <WysiwygField
              property="schema:text"
              html={body}
            />
          </div>

          {link && (
            <Button href={link.uri} secondary className={buildClass('link')}>
              {link.title}
            </Button>
          )}
        </div>

        <div className={joinClasses(buildClass('media-container'), gridClassesMedia)}>
          <Media media={media} />
        </div>
      </div>
    </article>
  );
};

export default ParagraphTextImage;
